/**
 * Premium pagination — the dashboard's replacement for MUI's
 * TablePagination.
 *
 * Lived inside FollowUpTracker until a second list (the No Shipments
 * Yet tab) needed the same control. Two lists on the same page paging
 * in two different visual languages would read as an accident, so it
 * moved here rather than being copied.
 *
 * Page numbers are ZERO-BASED, matching the tracker's existing state.
 */

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { Box, FormControl, MenuItem, Select, Tooltip, Typography } from '@mui/material';

import { colors } from '../theme';

// ----------------------------------------------------------------
// Premium pagination — replaces MUI's TablePagination with a custom
// implementation that matches the dashboard's premium feel:
//   * Numbered page pills (current page filled in brand color)
//   * Smart truncation with "…" for large page counts
//   * First / Prev / Next / Last icon buttons in their own group
//   * Styled rows-per-page Select with the same chip-style affordance
//   * Range display ("26–50 of 81") on the left
// ----------------------------------------------------------------

const ROWS_PER_PAGE_OPTIONS: { label: string; value: number }[] = [
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: 'All', value: -1 },
];

interface PremiumPaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
}

export function PremiumPagination({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: PremiumPaginationProps) {
  const allRows = rowsPerPage === -1;
  const pageCount = allRows ? 1 : Math.max(1, Math.ceil(count / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const from = allRows ? 1 : safePage * rowsPerPage + 1;
  const to = allRows ? count : Math.min((safePage + 1) * rowsPerPage, count);
  const pageTokens = buildPageTokens(pageCount, safePage);

  const isFirst = safePage === 0;
  const isLast = safePage >= pageCount - 1;

  return (
    <Box
      sx={{
        mt: 1,
        pt: 1.5,
        borderTop: `1px solid ${colors.borderLight}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {/* Left — range + rows per page selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography
          sx={{
            fontSize: 12,
            color: colors.textSecondary,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {allRows ? (
            <>
              <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                {count}
              </Box>{' '}
              of {count}
            </>
          ) : (
            <>
              <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                {from}–{to}
              </Box>{' '}
              of {count}
            </>
          )}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>Rows</Typography>
          <FormControl size="small">
            <Select
              value={rowsPerPage}
              onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              renderValue={(v) =>
                ROWS_PER_PAGE_OPTIONS.find((o) => o.value === v)?.label ?? String(v)
              }
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                color: colors.textPrimary,
                bgcolor: '#fff',
                borderRadius: 1,
                '& .MuiSelect-select': { py: 0.45, pr: 3, pl: 1.25 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.textMuted },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.chartPrimary,
                  borderWidth: 1,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    borderRadius: 1.5,
                    border: `1px solid ${colors.borderLight}`,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
                    '& .MuiMenu-list, & .MuiList-root': { py: 0.25 },
                  },
                },
              }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((o) => (
                <MenuItem
                  key={o.value}
                  value={o.value}
                  sx={{
                    fontSize: 12.5,
                    py: 0.6,
                    px: 1.25,
                    minHeight: 'unset',
                    fontWeight: o.value === rowsPerPage ? 600 : 500,
                    color: o.value === rowsPerPage ? colors.chartPrimary : colors.textPrimary,
                  }}
                >
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Right — page controls. Hidden when there's only one page. */}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <PageIconButton
            icon={<FirstPageIcon sx={{ fontSize: 16 }} />}
            label="First page"
            disabled={isFirst}
            onClick={() => onPageChange(0)}
          />
          <PageIconButton
            icon={<ChevronLeftIcon sx={{ fontSize: 16 }} />}
            label="Previous page"
            disabled={isFirst}
            onClick={() => onPageChange(safePage - 1)}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mx: 0.5 }}>
            {pageTokens.map((token, i) =>
              token === 'ellipsis' ? (
                <Box
                  key={`ell-${i}`}
                  component="span"
                  sx={{
                    width: 20,
                    textAlign: 'center',
                    fontSize: 12,
                    color: colors.textMuted,
                    userSelect: 'none',
                  }}
                >
                  …
                </Box>
              ) : (
                <PagePill
                  key={token}
                  page={token}
                  active={token === safePage}
                  onClick={() => onPageChange(token)}
                />
              ),
            )}
          </Box>

          <PageIconButton
            icon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
            label="Next page"
            disabled={isLast}
            onClick={() => onPageChange(safePage + 1)}
          />
          <PageIconButton
            icon={<LastPageIcon sx={{ fontSize: 16 }} />}
            label="Last page"
            disabled={isLast}
            onClick={() => onPageChange(pageCount - 1)}
          />
        </Box>
      )}
    </Box>
  );
}

interface PagePillProps {
  page: number;
  active: boolean;
  onClick: () => void;
}

function PagePill({ page, active, onClick }: PagePillProps) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        // Custom button so the focus ring is ours, not the browser default.
        appearance: 'none',
        border: `1px solid ${active ? colors.chartPrimary : 'transparent'}`,
        bgcolor: active ? colors.chartPrimary : 'transparent',
        color: active ? '#fff' : colors.textSecondary,
        fontSize: 12,
        fontWeight: active ? 700 : 500,
        minWidth: 28,
        height: 28,
        px: 0.75,
        borderRadius: 1,
        cursor: 'pointer',
        fontFeatureSettings: '"tnum"',
        transition: 'all 0.12s',
        boxShadow: active ? `0 1px 3px ${colors.chartPrimary}40` : 'none',
        '&:hover': {
          bgcolor: active ? colors.chartPrimary : colors.surfaceSubtle,
          color: active ? '#fff' : colors.textPrimary,
        },
        '&:focus-visible': {
          outline: `2px solid ${colors.chartPrimary}55`,
          outlineOffset: 1,
        },
      }}
    >
      {page + 1}
    </Box>
  );
}

interface PageIconButtonProps {
  icon: React.ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
}

function PageIconButton({ icon, label, disabled, onClick }: PageIconButtonProps) {
  return (
    <Tooltip title={label} placement="top" arrow enterDelay={400}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Box
          component="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          sx={{
            appearance: 'none',
            border: 'none',
            bgcolor: 'transparent',
            color: disabled ? colors.borderLight : colors.textSecondary,
            cursor: disabled ? 'default' : 'pointer',
            width: 28,
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            transition: 'background-color 0.12s, color 0.12s',
            '&:hover': disabled ? {} : { bgcolor: colors.surfaceSubtle, color: colors.textPrimary },
            '&:focus-visible': {
              outline: `2px solid ${colors.chartPrimary}55`,
              outlineOffset: 1,
            },
          }}
        >
          {icon}
        </Box>
      </Box>
    </Tooltip>
  );
}

// Build the sequence of page tokens shown in the pagination row.
// Always shows: 1, last, current, and ±1 around current. Anything else
// collapses to "…". Examples (with current marked **):
//   total=4,  cur=1: [1, **2**, 3, 4]
//   total=10, cur=0: [**1**, 2, 3, 4, …, 10]
//   total=10, cur=4: [1, …, 4, **5**, 6, …, 10]
//   total=10, cur=9: [1, …, 7, 8, 9, **10**]
function buildPageTokens(total: number, current: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const tokens: (number | 'ellipsis')[] = [0];
  const left = Math.max(1, current - 1);
  const right = Math.min(total - 2, current + 1);
  if (left > 1) tokens.push('ellipsis');
  for (let i = left; i <= right; i++) tokens.push(i);
  if (right < total - 2) tokens.push('ellipsis');
  tokens.push(total - 1);
  return tokens;
}
